import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Shared schemas                                                              */
/* -------------------------------------------------------------------------- */

export const Semver = z.string().regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, "semver");
export const DesignId = z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
export const Channel = z.enum(["latest", "beta"]);
export type Channel = z.infer<typeof Channel>;

export const Params = z.record(z.string(), z.unknown());
export type Params = z.infer<typeof Params>;

/** Plain user text: control characters removed (newlines kept when `multiline`), trimmed, then length-checked. */
function plainText(max: number, multiline = false) {
  const strip = multiline ? /[\u0000-\u0009\u000b-\u001f\u007f]/g : /[\u0000-\u001f\u007f]/g;
  return z
    .string()
    .transform((s) => s.replace(strip, multiline ? "" : " ").trim())
    .pipe(z.string().max(max));
}

/** Canonical form of a fit-tag ref: trimmed, lowercased, inner whitespace removed. */
export function normalizeFitRef(ref: string): string {
  return ref.trim().toLowerCase().replace(/\s+/g, "");
}
export const FIT_REF_PATTERN = /^[a-z0-9][a-z0-9.-]{0,31}$/;
export const MAX_FITS = 10;

/**
 * Something a configuration is known to fit (for brickbrackets: a LEGO set number).
 * Generic on purpose; the brand decides the label and a stricter client-side pattern.
 */
export const FitTag = z.object({
  ref: z.string().transform(normalizeFitRef).pipe(z.string().regex(FIT_REF_PATTERN, "fit ref")),
  name: plainText(80).optional(),
});
export type FitTag = z.infer<typeof FitTag>;

/** Up to MAX_FITS tags, deduplicated by ref (first wins). */
export const FitTags = z
  .array(FitTag)
  .max(MAX_FITS)
  .transform((tags) => tags.filter((t, i) => tags.findIndex((o) => o.ref === t.ref) === i));

export const Notes = plainText(1000, true);
export const DisplayName = z.string().regex(/^[A-Za-z0-9_.-]{3,30}$/, "3–30 letters, digits, _ . -");
export const Visibility = z.enum(["private", "public"]);

export const Me = z.object({
  sub: z.string(),
  email: z.string().optional(),
  name: z.string().optional(),
  picture: z.string().optional(),
  /** Public handle shown on community entries; never the email. */
  displayName: z.string().optional(),
  brand: z.string(),
  plan: z.enum(["free", "pro", "staff"]).default("free"),
  createdAt: z.string(),
});
export type Me = z.infer<typeof Me>;

export const SetProfileBody = z.object({ displayName: DisplayName });
export type SetProfileBody = z.infer<typeof SetProfileBody>;

export const DesignVersion = z.object({
  id: DesignId,
  version: Semver,
  pack: z.string(),
  bundleUrl: z.string(),
  hash: z.string(),
  kernel: z.enum(["manifold", "replicad"]).default("manifold"),
  jsonSchema: z.record(z.string(), z.unknown()),
  publishedAt: z.string(),
  changelog: z.string().optional(),
  deprecated: z.boolean().default(false),
  thumbnailUrl: z.string().optional(),
});
export type DesignVersion = z.infer<typeof DesignVersion>;

export const CatalogEntry = z.object({
  id: DesignId,
  pack: z.string(),
  title: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  thumbnailUrl: z.string().optional(),
  channel: Channel,
  /** Version the channel resolves to right now. */
  version: Semver,
  bundleUrl: z.string(),
  hash: z.string(),
  kernel: z.enum(["manifold", "replicad"]).default("manifold"),
  complexity: z.enum(["low", "medium", "high"]).optional(),
});
export type CatalogEntry = z.infer<typeof CatalogEntry>;

export const SavedConfig = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  designId: DesignId,
  version: Semver,
  params: Params,
  brand: z.string(),
  revision: z.number().int().min(1),
  fits: FitTags.default([]),
  /** The owner printed this and it fits. */
  printed: z.boolean().default(false),
  notes: Notes.optional(),
  visibility: Visibility.default("private"),
  publishedAt: z.string().optional(),
  thumbnailKey: z.string().optional(),
  shareToken: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedConfig = z.infer<typeof SavedConfig>;

export const CreateConfigBody = z.object({
  name: z.string().min(1).max(120),
  designId: DesignId,
  version: Semver,
  params: Params,
  fits: FitTags.optional(),
  printed: z.boolean().optional(),
  notes: Notes.optional(),
});
export type CreateConfigBody = z.input<typeof CreateConfigBody>;

/** Every update writes a new revision row; the config row points at the newest. */
export const UpdateConfigBody = z.object({
  name: z.string().min(1).max(120).optional(),
  version: Semver.optional(),
  params: Params.optional(),
  fits: FitTags.optional(),
  printed: z.boolean().optional(),
  notes: Notes.optional(),
});
export type UpdateConfigBody = z.input<typeof UpdateConfigBody>;

export const ConfigRevision = z.object({
  configId: z.string(),
  revision: z.number().int(),
  version: Semver,
  params: Params,
  name: z.string(),
  fits: FitTags.default([]),
  printed: z.boolean().default(false),
  notes: Notes.optional(),
  createdAt: z.string(),
});
export type ConfigRevision = z.infer<typeof ConfigRevision>;

/* -------------------------------------------------------------------------- */
/* Community (published configurations)                                        */
/* -------------------------------------------------------------------------- */

export const PublicConfigSummary = z.object({
  id: z.string(),
  designId: DesignId,
  version: Semver,
  name: z.string(),
  fits: FitTags.default([]),
  printed: z.boolean().default(false),
  authorName: z.string(),
  confirmCount: z.number().int().default(0),
  publishedAt: z.string(),
  updatedAt: z.string(),
});
export type PublicConfigSummary = z.infer<typeof PublicConfigSummary>;

export const PublicConfig = PublicConfigSummary.extend({
  params: Params,
  notes: z.string().optional(),
  bundleUrl: z.string(),
});
export type PublicConfig = z.infer<typeof PublicConfig>;

export const FitDirectoryEntry = z.object({ ref: z.string(), name: z.string().optional(), count: z.number().int() });
export type FitDirectoryEntry = z.infer<typeof FitDirectoryEntry>;

export const ReportReason = z.enum(["wrong-fit", "spam", "offensive", "other"]);
export type ReportReason = z.infer<typeof ReportReason>;
export const ReportBody = z.object({ reason: ReportReason, note: plainText(300).optional() });
export type ReportBody = z.input<typeof ReportBody>;

export const ModQueueItem = z.object({
  id: z.string(),
  name: z.string(),
  designId: DesignId,
  authorName: z.string(),
  status: z.enum(["live", "hidden"]),
  reportCount: z.number().int(),
  reports: z.array(z.object({ reason: ReportReason, note: z.string().optional(), at: z.string() })),
  firstReportAt: z.string(),
});
export type ModQueueItem = z.infer<typeof ModQueueItem>;

export const ModActionBody = z.object({ action: z.enum(["hide", "restore", "remove", "dismiss"]) });
export type ModActionBody = z.infer<typeof ModActionBody>;

export const SharedConfig = z.object({
  token: z.string(),
  name: z.string(),
  designId: DesignId,
  version: Semver,
  params: Params,
  brand: z.string(),
  bundleUrl: z.string(),
  sharedAt: z.string(),
});
export type SharedConfig = z.infer<typeof SharedConfig>;

export const AnalyticsEvent = z.object({
  type: z.enum(["view", "configure", "export", "save", "share"]),
  designId: DesignId,
  version: Semver.optional(),
  props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
  ts: z.string().optional(),
});
export type AnalyticsEvent = z.infer<typeof AnalyticsEvent>;

export const ApiError = z.object({ error: z.string(), code: z.string().optional(), details: z.unknown().optional() });
export type ApiError = z.infer<typeof ApiError>;

/* -------------------------------------------------------------------------- */
/* Route table: one source of truth for the client and the Lambda router.     */
/* -------------------------------------------------------------------------- */

const PublicPage = z.object({
  items: z.array(PublicConfigSummary),
  cursor: z.string().optional(),
  /** Fit refs a `fit` search resolved to (e.g. "42115" -> ["42115-1"]); absent without `fit`. */
  matched: z.array(z.string()).optional(),
});

/** Order matters: literal paths (`/public/fits`) must precede `/public/:id` for the router and API Gateway. */
export const routes = {
  me: { method: "GET", path: "/me", auth: true, res: Me },
  setProfile: { method: "PUT", path: "/me/profile", auth: true, body: SetProfileBody, res: Me },
  catalog: { method: "GET", path: "/catalog", auth: false, res: z.object({ designs: z.array(CatalogEntry) }) },
  designVersions: {
    method: "GET",
    path: "/designs/:id/versions",
    auth: false,
    res: z.object({ versions: z.array(DesignVersion), channels: z.record(z.string(), Semver) }),
  },
  listConfigs: { method: "GET", path: "/configs", auth: true, res: z.object({ configs: z.array(SavedConfig) }) },
  createConfig: { method: "POST", path: "/configs", auth: true, body: CreateConfigBody, res: SavedConfig },
  getConfig: { method: "GET", path: "/configs/:id", auth: true, res: z.object({ config: SavedConfig, revisions: z.array(ConfigRevision) }) },
  updateConfig: { method: "PUT", path: "/configs/:id", auth: true, body: UpdateConfigBody, res: SavedConfig },
  deleteConfig: { method: "DELETE", path: "/configs/:id", auth: true, res: z.object({ ok: z.literal(true) }) },
  shareConfig: { method: "POST", path: "/configs/:id/share", auth: true, res: z.object({ token: z.string(), url: z.string() }) },
  getShared: { method: "GET", path: "/share/:token", auth: false, res: SharedConfig },
  publishConfig: { method: "POST", path: "/configs/:id/publish", auth: true, res: SavedConfig },
  unpublishConfig: { method: "DELETE", path: "/configs/:id/publish", auth: true, res: SavedConfig },
  listFits: { method: "GET", path: "/public/fits", auth: false, res: z.object({ fits: z.array(FitDirectoryEntry) }) },
  listPublic: { method: "GET", path: "/public", auth: false, res: PublicPage },
  getPublic: { method: "GET", path: "/public/:id", auth: false, res: PublicConfig },
  /** `added: false` when this user had already confirmed. */
  confirmPublic: { method: "POST", path: "/public/:id/confirm", auth: true, res: z.object({ added: z.boolean(), confirmCount: z.number().int() }) },
  reportPublic: { method: "POST", path: "/public/:id/report", auth: true, body: ReportBody, res: z.object({ ok: z.literal(true) }) },
  modQueue: { method: "GET", path: "/mod/queue", auth: true, res: z.object({ items: z.array(ModQueueItem) }) },
  modAction: { method: "POST", path: "/mod/public/:id", auth: true, body: ModActionBody, res: z.object({ ok: z.literal(true) }) },
  track: { method: "POST", path: "/events", auth: false, body: z.object({ events: z.array(AnalyticsEvent).max(50) }), res: z.object({ ok: z.literal(true) }) },
} as const;

export type RouteName = keyof typeof routes;
export const BRAND_HEADER = "x-cfg-brand";

/* -------------------------------------------------------------------------- */
/* Typed fetch client                                                          */
/* -------------------------------------------------------------------------- */

export interface ApiClientOptions {
  baseUrl: string;
  brand: string;
  getToken?: () => Promise<string | null> | string | null;
  fetch?: typeof fetch;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError | undefined,
    message: string,
  ) {
    super(message);
  }
}

function fill(path: string, params: Record<string, string>): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, k: string) => encodeURIComponent(params[k]));
}

export function createApiClient(opts: ApiClientOptions) {
  const f = opts.fetch ?? fetch;
  async function call<TRes>(
    route: { method: string; path: string; auth: boolean; res: z.ZodType<TRes> },
    args: { params?: Record<string, string>; body?: unknown; query?: Record<string, string | undefined> } = {},
  ): Promise<TRes> {
    const headers: Record<string, string> = { accept: "application/json", [BRAND_HEADER]: opts.brand };
    if (args.body !== undefined) headers["content-type"] = "application/json";
    const token = await opts.getToken?.();
    if (token) headers.authorization = `Bearer ${token}`;
    else if (route.auth) throw new ApiRequestError(401, { error: "Sign in required" }, "Sign in required");
    const qs = new URLSearchParams(Object.entries(args.query ?? {}).filter((e): e is [string, string] => !!e[1])).toString();
    const res = await f(opts.baseUrl.replace(/\/$/, "") + fill(route.path, args.params ?? {}) + (qs ? `?${qs}` : ""), {
      method: route.method,
      headers,
      body: args.body === undefined ? undefined : JSON.stringify(args.body),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!res.ok) {
      const parsed = ApiError.safeParse(json);
      throw new ApiRequestError(res.status, parsed.success ? parsed.data : undefined, parsed.success ? parsed.data.error : `HTTP ${res.status}`);
    }
    return route.res.parse(json);
  }

  return {
    me: () => call(routes.me),
    catalog: () => call(routes.catalog),
    designVersions: (id: string) => call(routes.designVersions, { params: { id } }),
    listConfigs: () => call(routes.listConfigs),
    createConfig: (body: CreateConfigBody) => call(routes.createConfig, { body }),
    getConfig: (id: string) => call(routes.getConfig, { params: { id } }),
    updateConfig: (id: string, body: UpdateConfigBody) => call(routes.updateConfig, { params: { id }, body }),
    deleteConfig: (id: string) => call(routes.deleteConfig, { params: { id } }),
    shareConfig: (id: string) => call(routes.shareConfig, { params: { id } }),
    getShared: (token: string) => call(routes.getShared, { params: { token } }),
    setProfile: (body: SetProfileBody) => call(routes.setProfile, { body }),
    publishConfig: (id: string) => call(routes.publishConfig, { params: { id } }),
    unpublishConfig: (id: string) => call(routes.unpublishConfig, { params: { id } }),
    listFits: () => call(routes.listFits),
    listPublic: (q: { fit?: string; design?: string; cursor?: string; limit?: number } = {}) =>
      call(routes.listPublic, { query: { fit: q.fit, design: q.design, cursor: q.cursor, limit: q.limit?.toString() } }),
    getPublic: (id: string) => call(routes.getPublic, { params: { id } }),
    confirmPublic: (id: string) => call(routes.confirmPublic, { params: { id } }),
    reportPublic: (id: string, body: ReportBody) => call(routes.reportPublic, { params: { id }, body }),
    modQueue: () => call(routes.modQueue),
    modAction: (id: string, body: ModActionBody) => call(routes.modAction, { params: { id }, body }),
    track: (events: AnalyticsEvent[]) => call(routes.track, { body: { events } }),
  };
}
export type ApiClient = ReturnType<typeof createApiClient>;
